import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store';
import { Suggestion } from '../redux/suggestions';
import { submitTestingMetrics, EvaluatedPromptResponse, setRunIndex, setExperimentId, setIsCancelled, setIsRunning, resetExperiment } from '../redux/experiments';
import { TestingFrameworkConfiguration } from '../constants';
import { FetchLoadingStateEnum } from '../constants';
import { WordApiService } from '../services/word/api';
import { fetchAnalysisLoadingState, selectSuggestions } from '../selectors/suggestions';
import { v4 as uuidv4 } from 'uuid';
import { useAnalyzeReport } from './useAnalyzeReport';
import { selectExperimentId, selectIsCancelled, selectIsRunning, selectRunIndex } from '../selectors/experiments';

export const useExperimentRunner = () => {
  const { startAnalyzeReport } = useAnalyzeReport();
  const dispatch = useDispatch<AppDispatch>();
  const evaluatedPromptResponses = useRef<EvaluatedPromptResponse[]>([]);
  const [pendingExecutionId, setPendingExecutionId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const loading: FetchLoadingStateEnum = useSelector(fetchAnalysisLoadingState);
  const runIndex = useSelector(selectRunIndex);
  const isRunning = useSelector(selectIsRunning);
  const isCancelled = useSelector(selectIsCancelled);
  const experimentId = useSelector(selectExperimentId);

  const findMatchInDocument = async (text: string): Promise<boolean> => {
    const wordApi = new WordApiService();
    const result = await wordApi.withContext(async () => {
      return Boolean(await wordApi.findMatchInDocument(text));
    });
    return result as boolean;
  };
  const suggestions = useSelector(selectSuggestions).flat();
  const [waitingForNextRun, setWaitingForNextRun] = useState(false);

  const kickOffRun = async (nextIndex: number) => {
    const executionId = uuidv4();
    setPendingExecutionId(executionId);
    dispatch(setRunIndex(nextIndex));
    setWaitingForNextRun(true);

    try {
      await startAnalyzeReport({ useMockResponse: false, itpExecutionId: executionId });
    } catch (error) {
      console.error('Error during kickOffRun:', error); // TODO: Implement error handling
    }
  };

  const runExperiment = async () => {
    const url = Office.context.document.url;
    const fileName = url?.split("/")?.pop() || "document.docx";
    setFileName(fileName);
    dispatch(setExperimentId(experimentId));
    dispatch(setIsRunning(true));
    dispatch(setIsCancelled(false));
    evaluatedPromptResponses.current = [];
    setWaitingForNextRun(false);
    await kickOffRun(1);
  };

  const cancelExperiment = () => {
    console.warn('Experiment cancelled. Halting further runs.');
    dispatch(setIsCancelled(true));
  };

  useEffect(() => {
    const handleRunLifecycle = async () => {
      if (!pendingExecutionId || !isRunning || waitingForNextRun) return;
      if (![FetchLoadingStateEnum.fulfilled, FetchLoadingStateEnum.error].includes(loading)) return;

      if (loading === FetchLoadingStateEnum.fulfilled) {
        const baseTimestamp = Date.now();
        const results = await Promise.all(
          suggestions.map(async (suggestion: Suggestion, index: number) => {
            let matchFound = false;
            let matchContent = '';

            if (suggestion.citations && suggestion.citations.length > 0) {
              for (const citation of suggestion.citations) {
                const textToMatch = citation.text;
                if (textToMatch) {
                  matchFound = await findMatchInDocument(textToMatch);
                  if (matchFound) {
                    matchContent = textToMatch;
                    break;
                  }
                }
              }
            }

            const result = {
              itpExecutionId: pendingExecutionId,
              documentIdentifier: fileName,
              promptIdentifier: suggestion.promptIdentifier,
              matchContent,
              matchFound,
              experimentId,
              createdAt: new Date(baseTimestamp + index).toISOString() // offset to ensure uniqueness
            };
            return result;
          })
        );

        evaluatedPromptResponses.current.push(...results);
        console.log(`Run ${runIndex} complete`, { results });
      } else {
        console.warn(`Run ${runIndex} failed, skipping.`);
      }

      const nextIndex = runIndex + 1;

      if (isCancelled || nextIndex > TestingFrameworkConfiguration.runs) {
        await dispatch(submitTestingMetrics(evaluatedPromptResponses.current));
        dispatch(resetExperiment());
        setPendingExecutionId(null);
        setWaitingForNextRun(false);
        return;
      }

      await kickOffRun(nextIndex);
    };

    handleRunLifecycle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pendingExecutionId, runIndex, suggestions, waitingForNextRun]);

  useEffect(() => {
    if (waitingForNextRun && suggestions.length > 0) {
      console.log(`Run ${runIndex}: New analysis started with ${suggestions.length} suggestions`);
      setWaitingForNextRun(false);
    }
  }, [suggestions, waitingForNextRun, runIndex]);

  return {
    isRunning,
    runIndex,
    experimentId,
    isCancelled,
    setExperimentId,
    runExperiment,
    cancelExperiment
  };
};
